import { useState } from "react";
import { Pencil, Save, X, Loader2, RotateCcw, Hash, CalendarDays, Sigma } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { Markdown } from "@/components/course/Markdown";
import { Generating, ErrorBox } from "@/components/course/States";

const Side = ({ c }) => (
  <aside className="space-y-4">
    {c.summary && <div className="card-flat p-5"><p className="eyebrow mb-2">Résumé</p><p className="text-sm leading-relaxed" data-testid="course-summary">{c.summary}</p></div>}
    {c.keywords?.length > 0 && (
      <div className="card-flat p-5"><p className="eyebrow mb-3 flex items-center gap-1.5"><Hash className="h-3.5 w-3.5" /> Mots-clés</p>
        <div className="flex flex-wrap gap-1.5">{c.keywords.map((k) => <span key={k} className="rounded-full bg-accent px-2.5 py-1 text-xs font-medium text-accent-foreground">{k}</span>)}</div></div>
    )}
    {c.dates?.length > 0 && (
      <div className="card-flat p-5"><p className="eyebrow mb-3 flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" /> Dates</p>
        <ul className="space-y-2 text-sm">{c.dates.map((d, i) => <li key={i}><b>{d.date}</b> — <span className="text-muted-foreground">{d.event}</span></li>)}</ul></div>
    )}
    {c.formulas?.length > 0 && (
      <div className="card-flat p-5"><p className="eyebrow mb-3 flex items-center gap-1.5"><Sigma className="h-3.5 w-3.5" /> Formules</p>
        <ul className="space-y-2 text-sm">{c.formulas.map((f, i) => <li key={i}><code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">{f.formula}</code> <span className="text-muted-foreground">{f.description}</span></li>)}</ul></div>
    )}
  </aside>
);

export const NotesTab = ({ c, setCourse, reload }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const relaunch = async () => {
    try { await api.post(`/courses/${c.course_id}/regenerate`); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (c.status === "processing")
    return <Generating label={c.source === "import" && c.progress < 1 ? `Transcription du fichier… ${Math.round((c.progress || 0) * 100)} %` : "L'IA rédige tes notes structurées…"} sub="Cela prend en général 1 à 3 minutes. Tu peux quitter cette page, le traitement continue." />;
  if (c.status === "recording")
    return <ErrorBox message="Cet enregistrement n'a pas été terminé. Tu peux générer les notes à partir de ce qui a déjà été transcrit." action={<button data-testid="generate-from-partial-button" onClick={relaunch} className="bg-brand rounded-full px-5 py-2.5 text-sm font-semibold text-white">Générer les notes</button>} />;
  if (c.status === "error")
    return <ErrorBox message={c.error || "La génération a échoué."} action={c.transcript ? <button data-testid="regenerate-notes-button" onClick={relaunch} className="inline-flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-semibold hover:border-primary/40"><RotateCcw className="h-4 w-4" /> Relancer la génération</button> : null} />;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/courses/${c.course_id}`, { notes_markdown: draft });
      setCourse(data);
      setEditing(false);
      toast.success("Notes enregistrées.");
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <div className="grid gap-5 lg:grid-cols-[1fr_300px]">
      <div className="card-flat p-6 sm:p-9">
        <div className="mb-5 flex justify-end gap-2">
          {editing ? (
            <>
              <button data-testid="cancel-edit-notes-button" onClick={() => setEditing(false)} className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold hover:bg-muted"><X className="h-4 w-4" /> Annuler</button>
              <button data-testid="save-notes-button" onClick={save} disabled={saving} className="bg-brand inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold text-white">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Enregistrer</button>
            </>
          ) : (
            <button data-testid="edit-notes-button" onClick={() => { setDraft(c.notes_markdown); setEditing(true); }} className="inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-semibold hover:border-primary/40"><Pencil className="h-4 w-4" /> Modifier les notes</button>
          )}
        </div>
        {editing ? (
          <>
            <Textarea data-testid="notes-editor-textarea" value={draft} onChange={(e) => setDraft(e.target.value)} className="min-h-[560px] rounded-xl bg-background font-mono text-[13px] leading-relaxed" />
            <p className="mt-2 text-xs text-muted-foreground">Format Markdown : # Titre, ## Sous-titre, **gras**, - liste, &gt; définition.</p>
          </>
        ) : (
          <div data-testid="course-notes-content"><Markdown>{c.notes_markdown}</Markdown></div>
        )}
      </div>
      <Side c={c} />
    </div>
  );
};
